"use client";

import { useEffect } from "react";
import {
  contrastingForeground,
  FOREGROUND,
  FOREGROUND_ALT,
  mutedFrom,
} from "@/lib/colors";
import { loadFont } from "@/lib/fonts";
import type { LinkConfig } from "@/lib/schemas/paycode/link-config";

const CSS_VARS = {
  accentColor: "--accent",
  surfaceColor: "--surface",
  surfaceAltColor: "--surface-alt",
  backgroundColor: "--background",
  topColor: "--top",
  textForegroundColor: "--foreground",
  textForegroundAltColor: "--foreground-alt",
  textMuted: "--muted",
  textMutedAlt: "--muted-alt",
  borderColor: "--border",
  borderColorAlt: "--border-alt",
} as const;

const SURFACE_PAIRS = [
  {
    surface: "surfaceColor",
    foreground: "textForegroundColor",
    muted: "textMuted",
    fallback: FOREGROUND,
    surfaceVar: "--surface",
    fgVar: "--foreground",
    mutedVar: "--muted",
  },
  {
    surface: "surfaceAltColor",
    foreground: "textForegroundAltColor",
    muted: "textMutedAlt",
    fallback: FOREGROUND_ALT,
    surfaceVar: "--surface-alt",
    fgVar: "--foreground-alt",
    mutedVar: "--muted-alt",
  },
] as const;

const PersonalizeLink = (config: Partial<LinkConfig>) => {
  useEffect(() => {
    const { style } = document.documentElement;

    for (const [key, cssVar] of Object.entries(CSS_VARS)) {
      const color = config[key as keyof typeof CSS_VARS];
      if (color) style.setProperty(cssVar, color);
    }

    for (const pair of SURFACE_PAIRS) {
      const surface = config[pair.surface];
      if (!surface) continue;

      if (!config[pair.foreground])
        style.setProperty(
          pair.fgVar,
          contrastingForeground(surface, pair.fallback),
        );

      if (!config[pair.muted])
        style.setProperty(
          pair.mutedVar,
          mutedFrom(pair.fgVar, pair.surfaceVar),
        );
    }

    if (config.accentColor)
      style.setProperty("--selection", `${config.accentColor}4d`);

    if (config.contentFont) loadFont(config.contentFont, "--content-font");
    if (config.businessFont) loadFont(config.businessFont, "--business-font");
  }, [config]);

  return null;
};

export default PersonalizeLink;
